import React, { useCallback, useEffect } from 'react';
import { RouteComponentProps, Link } from '@reach/router';
import { makeStyles, useLoading, useUseCase } from '@smile-next/uikit';
import { LoginForm } from '@/components/LoginForm/LoginForm';
import { LoginFormBody } from '@/components/LoginForm/LoginFormBody';
import { LoginFormFooter } from '@/components/LoginForm/LoginFormFooter';
import { LoginFormHeader } from '@/components/LoginForm/LoginFormHeader';
import { NextAccountLoginPageState } from '@/containers/NextAccountLogin/Page/NextAccountLoginPageState';
import {
    NextAccountLoginFormValues,
    NextAccountLoginForm,
} from '@/containers/NextAccountLogin/Page/components/NextAccountLoginForm';
import { useLocationSearch } from '@/hooks/useLocationSearch';
import { useRouter } from '@/hooks/useRouter';
import { ConfirmCloudIdUseCase } from '@/usecases/ConfirmCloudIdUseCase';
import { InitializeNextAccountLoginFormUseCase } from '@/usecases/InitializeNextAccountLoginFormUseCase';
import { LoginNextAccountUseCase } from '@/usecases/LoginNextAccountUseCase';
import { VerifyAuthenticatedUserUseCase } from '@/usecases/VerifyAuthenticatedUserUseCase';

const useStyles = makeStyles((theme) => ({
    root: {
        height: 'calc(100vh - 56px)',
        display: 'flex',
        flexDirection: 'column',
    },
    main: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        flex: '1 1 auto',
    },
    link: {
        color: theme.palette.primary.main,
    },
    confirmCloudId: {
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.primary.main,
        textDecoration: 'underline',
        cursor: 'pointer',
        userSelect: 'none',
    },
    errorMessage: {
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.error.main,
    },
}));

export const NextAccountLoginPage: React.FC<RouteComponentProps> = () => {
    const { state, usecase } = useUseCase(new NextAccountLoginPageState());
    const { cloudId, loginId } = useLocationSearch();
    const { movePageAfterSuccessfulLogin, toForceSelectLoginPagePath } = useRouter();
    const { startCoverLoading, startLockLoading, endLoadingByName } = useLoading();
    const classes = useStyles();

    useEffect(() => {
        startCoverLoading('NextAccountLoginVerifyAuthenticatedUser');
    }, [startCoverLoading]);

    useEffect(() => {
        if (state.isAuthenticated === undefined) {
            // 初回レンダー時は無視する
        } else if (state.isAuthenticated) {
            movePageAfterSuccessfulLogin();
        }
    }, [movePageAfterSuccessfulLogin, state.isAuthenticated]);

    useEffect(() => {
        // 現在の認証情報を検証する
        usecase(new VerifyAuthenticatedUserUseCase())
            .execute()
            .catch(() => {
                // エラー発生時の処理は別途行っているためここでは何も行わない
            })
            .finally(() => {
                endLoadingByName('NextAccountLoginVerifyAuthenticatedUser');
            });

        // フォームで使用するstate値の初期化
        usecase(new InitializeNextAccountLoginFormUseCase()).execute({
            cloudId,
            loginId,
        });
    }, [cloudId, endLoadingByName, loginId, usecase]);

    const handleClickLoginButton = async (nextAccountLoginFormProps: NextAccountLoginFormValues) => {
        const { endLoading } = startLockLoading();
        await usecase(new LoginNextAccountUseCase()).execute({
            cloudId: nextAccountLoginFormProps.cloudId,
            loginId: nextAccountLoginFormProps.loginId,
            password: nextAccountLoginFormProps.password,
            isDisplayedCloudId: nextAccountLoginFormProps.isDisplayedCloudId,
        });
        endLoading();
    };

    const handleClickConfirmCloudId = useCallback(async () => {
        await usecase(new ConfirmCloudIdUseCase()).execute();
    }, [usecase]);

    const handleKeyDownConfirmCloudId = useCallback(
        async (e) => {
            if (e.key === 'Enter') {
                await handleClickConfirmCloudId();
            }
        },
        [handleClickConfirmCloudId],
    );

    return (
        <div className={classes.root}>
            <div className={classes.main}>
                <LoginForm>
                    <LoginFormHeader />
                    <LoginFormBody>
                        <NextAccountLoginForm
                            cloudId={state.authParams?.cloudId}
                            loginId={state.loginId}
                            password={state.password}
                            shouldShowCloudId={state.shouldShowCloudId}
                            onClickLoginButton={handleClickLoginButton}
                        />
                        {state.errorMessage && <div className={classes.errorMessage}>{state.errorMessage}</div>}
                        {state.shouldShowConfirmCloudId && (
                            <div
                                className={classes.confirmCloudId}
                                tabIndex={0}
                                onClick={handleClickConfirmCloudId}
                                onKeyDown={handleKeyDownConfirmCloudId}
                            >
                                クラウドIDを確認する
                            </div>
                        )}
                    </LoginFormBody>
                    <LoginFormFooter>
                        <Link className={classes.link} to={toForceSelectLoginPagePath(state.authParams)}>
                            別の方法でログイン
                        </Link>
                    </LoginFormFooter>
                </LoginForm>
            </div>
        </div>
    );
};
